import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useCurrentTheme } from 'app/shared/theme';
import { useAnalyticsContext } from 'app/shared/utils';
import { Event } from 'app/shared/typings';
import { EventForCompactEventCard } from 'app/shared/typings';
import { Typography } from 'app/shared/components/atoms';
import { Grid, GridItem } from 'app/shared/components/atoms';
import useOnScreen from 'app/fan/utils/useOnScreen';
import { CompactEventCard } from 'app/fan/components/organisms';

import { RecommendedEventsNavigationButton } from './RecommendedEventsNavigationButton';

interface RecommendedEventsProps {
  event?: Event;
  recommendedEvents: EventForCompactEventCard[];
  title?: string;
  forceVerticalLayout?: boolean;
  ticketSaleStatus?: string;
}

export const RecommendedEvents: React.FC<
  React.PropsWithChildren<RecommendedEventsProps>
> = ({
  event,
  recommendedEvents,
  title,
  forceVerticalLayout = false,
  ticketSaleStatus,
}) => {
  const { formatMessage } = useIntl();
  const { colors } = useCurrentTheme();

  const ref: any = useRef<HTMLDivElement>();
  const onScreen: boolean = useOnScreen<HTMLDivElement>(ref, '-150px');

  const [userSawThisComponent, setUserSawThisComponent] = useState(false);

  const { trackAnalyticsEvent } = useAnalyticsContext();

  useEffect(() => {
    if (!userSawThisComponent && onScreen) {
      setUserSawThisComponent(true);
      trackAnalyticsEvent('Cross Sell Events Entered Viewport', {
        event_id: event?.id,
        ticket_sale_status: ticketSaleStatus,
      });
    }
  }, [event?.id, onScreen, trackAnalyticsEvent, userSawThisComponent]);

  if (!event || recommendedEvents.length === 0) {
    return null;
  }

  const sendProductClickedEvent = (event: Event) => {
    if (event) {
      trackAnalyticsEvent('Event Page Recommended Product Clicked', {
        event_id: event.id,
      });
    }
  };

  const smallDesktopNumberOfColumns = forceVerticalLayout ? 1 : 2;
  const largeDesktopNumberOfColumns = forceVerticalLayout ? 1 : 3;

  return (
    <Grid
      ref={ref}
      mobileGap={5}
      tabletGap={5}
      smallDesktopGap={8}
      largeDesktopGap={5}
      rowGap={8}
      mobileNumberOfColumns={1}
      tabletNumberOfColumns={1}
      smallDesktopNumberOfColumns={smallDesktopNumberOfColumns}
      largeDesktopNumberOfColumns={largeDesktopNumberOfColumns}
    >
      <GridItem
        mobile={1}
        smallDesktop={smallDesktopNumberOfColumns}
        largeDesktop={largeDesktopNumberOfColumns}
      >
        <Typography
          color={colors.primary.white.base}
          fontSize="fontSize500"
          fontWeight="medium"
          fontFamily="title"
          lineHeight="120%"
        >
          {title || formatMessage({ id: 'event.recommendedEvents.title' })}
        </Typography>
      </GridItem>
      {recommendedEvents.map((recommendedEvent: EventForCompactEventCard) => (
        <GridItem mobile={1} key={`event-card-${recommendedEvent.id}`}>
          <CompactEventCard
            event={recommendedEvent}
            city={event.city}
            onClick={() => sendProductClickedEvent(recommendedEvent as Event)}
            data-qaid="event-page-recommended-event-card-link"
          />
        </GridItem>
      ))}
      <GridItem
        mobile={1}
        smallDesktop={smallDesktopNumberOfColumns}
        largeDesktop={largeDesktopNumberOfColumns}
      >
        <RecommendedEventsNavigationButton cachedSlug={event.city.cachedSlug} />
      </GridItem>
    </Grid>
  );
};
